import React, { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { H1, Text } from '../components/ui/Typography';
import { Card } from '../components/ui/Card';
import SafeIcon from '../common/SafeIcon';
import { generateExecutorPDF } from '../utils/executorPdfGenerator';
import { translations } from '../translations';

const Tools = () => {
  const { lang = 'nl' } = useParams();
  const t = translations[lang]?.tools || translations.nl.tools;
  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownloadChecklist = async (e) => {
    e.preventDefault();
    setIsDownloading(true);
    try {
      await generateExecutorPDF();
    } finally {
      setTimeout(() => setIsDownloading(false), 1000);
    }
  };

  const tools = [
    { title: t.check.title, desc: t.check.desc, icon: 'FileText', status: lang === 'nl' ? 'Aanbevolen' : 'Recommended', path: `/${lang}/tools/testament-check`, active: true, cta: t.check.cta },
    { title: t.tax.title, desc: t.tax.desc, icon: 'Calculator', status: lang === 'nl' ? 'Populair' : 'Popular', path: `/${lang}/${lang === 'nl' ? 'tools/erfbelasting-analyse' : 'tools/inheritance-tax-analysis'}`, active: true, cta: t.tax.cta },
    { title: t.executor.title, desc: t.executor.desc, icon: 'List', status: lang === 'nl' ? 'NIEUW' : 'NEW', path: '#', active: true, cta: t.executor.cta, onClick: handleDownloadChecklist },
  ];

  return (
    <div className="max-w-7xl mx-auto px-6 py-20">
      <div className="max-w-3xl mb-16">
        <H1 className="mb-6">{t.title}</H1>
        <Text>{t.subtitle}</Text>
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
        {tools.map((tool, i) => {
          const Content = (
            <Card 
              onClick={tool.onClick}
              className={`p-10 h-full flex flex-col relative group transition-all duration-300 cursor-pointer ${!tool.active ? 'opacity-80 grayscale border-slate-100' : 'border-slate-100 hover:border-navy-900/20 hover:shadow-2xl hover:-translate-y-1'}`} 
              hover={tool.active}
            >
              {tool.status && (
                <span className={`absolute top-8 right-8 text-[9px] font-bold uppercase tracking-widest px-3 py-1 rounded-full ${tool.status === 'Aanbevolen' || tool.status === 'Recommended' ? 'bg-orange-500 text-white' : 'bg-navy-950 text-white'}`}>
                  {tool.status}
                </span>
              )}
              <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-8 transition-colors ${tool.active ? 'bg-slate-50 text-navy-900 group-hover:bg-navy-900 group-hover:text-white' : 'bg-slate-100 text-slate-400'}`}>
                <SafeIcon icon={tool.icon === 'FileText' ? 'FiFileText' : tool.icon === 'Calculator' ? 'FiCalculator' : 'FiList'} name={tool.icon} className="text-2xl" />
              </div>
              <h3 className="text-xl font-bold mb-4 text-navy-900">{tool.title}</h3>
              <Text subtle className="text-sm mb-10 leading-relaxed">
                {tool.desc}
              </Text>
              <div className="mt-auto flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.15em]">
                {tool.active ? (
                  <span className="text-navy-900 flex items-center gap-2">
                    {isDownloading && tool.title === t.executor.title ? (lang === 'nl' ? 'BEZIG MET DOWNLOADEN...' : 'DOWNLOADING...') : tool.cta}
                    {!isDownloading && <SafeIcon name="ArrowRight" className="group-hover:translate-x-1 transition-transform" />}
                    {isDownloading && tool.title === t.executor.title && <div className="w-3 h-3 border-2 border-navy-900 border-t-transparent rounded-full animate-spin" />}
                  </span>
                ) : (
                  <span className="text-slate-400 flex items-center gap-2">
                    {lang === 'nl' ? 'Binnenkort beschikbaar' : 'Coming soon'} <SafeIcon name="Lock" className="text-xs" />
                  </span>
                )}
              </div>
            </Card>
          );
          return tool.path === '#' ? <div key={i}>{Content}</div> : <Link to={tool.path} key={i}>{Content}</Link>;
        })}
      </div>
    </div>
  );
};

export default Tools;